import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
  useMobile,
} from '@octano/global-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../utils/dates';
import { PaymentHistoryFilters } from '../../../../FinancialPortal/PaymentHistory/types';

const dictPrefix =
  'financialPortal.currentAccount.tabs.paymentHistoryReceived.searchControls';

interface FormValues {
  paymentDate: string | null;
  voucherNumber: string;
  paymentType: SelectOptionType | null;
}

const DEFAULT_FORM_VALUES: FormValues = {
  paymentDate: null,
  voucherNumber: '',
  paymentType: null,
};

interface Props {
  paymentTypeOptions: SelectOptionType[];
  isDownloadLoading?: boolean;
  onSearch: (sp: PaymentHistoryFilters) => void;
  onClear: () => void;
  onDownload: () => void;
}

export function SearchControls({
  paymentTypeOptions,
  isDownloadLoading = false,
  onSearch,
  onClear,
  onDownload,
}: Props) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const submit = (values: FormValues) => {
    onSearch({
      paymentDate: values.paymentDate
        ? toDateOnlyPayload(values.paymentDate)
        : undefined,
      voucherNumber: values.voucherNumber?.trim() || undefined,
      paymentType: (values.paymentType?.value as string) || undefined,
    });
  };

  const handleClear = () => {
    reset(DEFAULT_FORM_VALUES);
    onClear();
  };

  return (
    <>
      <Row className="gutters-filters">
        <Col xs={12} md={3}>
          <TextOutlinedInput
            control={control}
            type={'date' as any}
            name="paymentDate"
            label={t(`${dictPrefix}.paymentDate.label`)}
            placeholder={t(`${dictPrefix}.paymentDate.placeholder`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            name="voucherNumber"
            control={control}
            label={t(`${dictPrefix}.voucherNumber.label`)}
            placeholder={t(`${dictPrefix}.voucherNumber.placeholder`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="paymentType"
            control={control}
            label={t(`${dictPrefix}.paymentType.label`)}
            placeholder={t(`${dictPrefix}.paymentType.placeholder`)}
            options={paymentTypeOptions}
          />
        </Col>
        <Col xs={12} md={3}>
          <div className="d-flex justify-content-end">
            <Button
              type="button"
              text={t(`${dictPrefix}.search`)}
              size="sm"
              onClick={handleSubmit(submit)}
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? 0 : '1.5rem',
              }}
            />
            <Button
              type="button"
              text={t(`${dictPrefix}.clear`)}
              size="sm"
              outlined
              onClick={handleClear}
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? '1rem' : '1.5rem',
                marginLeft: 10,
              }}
            />
          </div>
        </Col>
      </Row>
      <Row className="pt-3 pb-4">
        <Col xs={12} md={{ size: 3, offset: 9 }}>
          <Button
            type="button"
            icon="download"
            text={t(`${dictPrefix}.download`)}
            size="sm"
            outlined
            loading={isDownloadLoading}
            onClick={onDownload}
            style={{ width: '100%', height: 41 }}
          />
        </Col>
      </Row>
    </>
  );
}
